<script setup>
import { ref, computed, watch, inject, nextTick } from "vue";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/shared/ui/sheet";
import { Empty } from "@/shared/ui/empty";
import { Skeleton } from "@/shared/ui/skeleton";
import Button from "@/shared/ui/button/Button.vue";
import { Spinner } from "@/shared/ui/spinner";

import UserProfileSidebar from "./UserProfileSidebar.vue";
import PersonalTab from "./tabs/PersonalTab.vue";
import AccessTab from "./tabs/AccessTab.vue";
import SystemTab from "./tabs/SystemTab.vue";

import { useUserDrawerData } from "../composables/useUserDrawerData";
import { PROFILE_DRAWER_KEY } from "@/widgets/profile-drawer";
import { useConfirm } from "@/shared/ui/alert-dialog/useConfirm";
import { cn } from "@/shared/lib/utils";

const confirm = useConfirm();
const { t } = useI18n();

const profileDrawer = inject(PROFILE_DRAWER_KEY, null);
const store = useStore();
const hasAdminPerm = computed(() => !!store.getters.has_access?.("AUU"));

const TAB_COMPONENTS = {
  personal: PersonalTab,
  access: AccessTab,
  system: SystemTab,
};

const isOpen = ref(false);
const tab = ref("personal");
const closeCallback = ref(null);

const { state, dirty, load, update, cancel, startEdit, save, remove } =
  useUserDrawerData();

const showEmpty = computed(
  () =>
    !state.loading &&
    state.mode !== "create" &&
    (state.error || !Object.keys(state.data || {}).length)
);

const currentTab = computed(() => TAB_COMPONENTS[tab.value] || PersonalTab);

const tabBindings = computed(() => {
  if (tab.value === "personal") {
    return {
      data: state.data,
      editMode: state.editMode,
      canEditContacts: state.editMode,
      canEditName: state.editMode && hasAdminPerm.value,
    };
  }
  if (tab.value === "access") {
    return {
      data: state.data,
      organizations: state.organizations,
      editMode: state.editMode,
      canEdit: hasAdminPerm.value,
      self: state.self,
    };
  }
  return { data: state.data, editMode: state.editMode, canEdit: false };
});

watch(
  [tab, hasAdminPerm],
  ([currentTab, isAdmin]) => {
    if (currentTab === "system" && !isAdmin) {
      tab.value = "personal";
    }
  },
  { immediate: true }
);

const tabListeners = computed(() => ({ update }));

async function open(uuid, opts = {}) {
  if (!uuid && !opts.self) {
    console.warn("[UserProfileDrawer] open() requires a uuid");
    return;
  }
  closeCallback.value = opts.onClose || null;
  isOpen.value = true;
  tab.value = "personal";
  await load(uuid, "view", { self: opts.self });
}

async function openView(uuid, opts = {}) {
  if (!uuid) {
    console.warn("[UserProfileDrawer] openView() requires a uuid");
    return;
  }
  closeCallback.value = opts.onClose || null;
  isOpen.value = true;
  tab.value = "personal";
  await load(uuid, "view");
}

async function openEdit(uuid, opts = {}) {
  if (!uuid) {
    console.warn("[UserProfileDrawer] openEdit() requires a uuid");
    return;
  }
  closeCallback.value = opts.onClose || null;
  isOpen.value = true;
  tab.value = "personal";
  await load(uuid, "edit");
}

async function openCreate(opts = {}) {
  closeCallback.value = opts.onClose || null;
  isOpen.value = true;
  tab.value = "personal";
  await load(null, "create");
}

function requestClose() {
  if (state.editMode && dirty.value) {
    if (
      !window.confirm(
        t("Несохранённые изменения будут потеряны. Закрыть профиль?")
      )
    ) {
      return;
    }
  }
  doClose();
}

function doClose() {
  isOpen.value = false;
  state.editMode = false;
  state.mode = "view";
  if (closeCallback.value) {
    const cb = closeCallback.value;
    closeCallback.value = null;
    cb();
  }
}

function handleOpenChange(val) {
  if (!val) requestClose();
}

function onEditClick() {
  if (state.loading || showEmpty.value) return;
  startEdit();
}

async function onSave() {
  try {
    await save();
  } catch (e) {
    console.error(e);
  }
}

function onCancel() {
  if (state.mode === "create") {
    doClose();
    return;
  }
  cancel();
}

function onPointerDownOutside(event) {
  if (state.editMode && dirty.value) {
    event.preventDefault();
    if (
      window.confirm(
        t("Несохранённые изменения будут потеряны. Закрыть профиль?")
      )
    ) {
      doClose();
    }
  }
}

async function onDeleteUser() {
  if (!hasAdminPerm.value || state.deleting || !state.userUuid) return;
  const fullName =
    [state.data.last_name, state.data.first_name].filter(Boolean).join(" ") ||
    t("пользователя");
  try {
    await confirm(
      t("Действие необратимо. Будет удалён пользователь {fullName}.", {
        fullName,
      }),
      t("Удалить пользователя?"),
      {
        confirmButtonText: t("Удалить"),
        cancelButtonText: t("Отмена"),
        type: "danger",
      }
    );
  } catch (e) {
    return;
  }
  try {
    await remove();
    doClose();
  } catch (e) {
    console.error(e);
  }
}

const isOpenedUserNotary = computed(() => {
  const codes = Array.isArray(state.data?.role_codes)
    ? state.data.role_codes
    : [];
  if (codes.includes("NOTARY")) return true;
  const roles = Array.isArray(state.data?.roles) ? state.data.roles : [];
  return roles.some((r) => r?.code === "NOTARY");
});

const canShowSwitcher = computed(
  () =>
    !!profileDrawer &&
    state.mode !== "create" &&
    !!state.userUuid &&
    isOpenedUserNotary.value
);

function onSwitchView(view) {
  if (view !== "notary" || !profileDrawer) return;
  if (
    state.editMode &&
    dirty.value &&
    !window.confirm(t("Несохранённые изменения будут потеряны. Сменить вид?"))
  ) {
    return;
  }
  profileDrawer.open(state.userUuid);
  nextTick(() => doClose());
}

const saveLabel = computed(() => {
  if (state.saving) return t("Сохраняем…");
  if (state.mode === "create") return t("Создать");
  return t("Сохранить");
});

const canSave = computed(() => {
  if (state.saving) return false;
  if (state.mode === "create") return true;
  if (state.mode === "edit") return dirty.value;
  return false;
});

defineExpose({
  open,
  openCreate,
  openEdit,
  openView,
  close: doClose,
});
</script>

<template>
  <Sheet :open="isOpen" @update:open="handleOpenChange">
    <SheetContent
      side="right"
      :class="
        cn(
          'p-0 flex flex-col gap-0 sm:max-w-none [&>button]:hidden border border-solid border-border shadow-sm bg-card'
        )
      "
      :style="{ width: 'min(1240px, 96vw)', maxWidth: 'min(1240px, 96vw)' }"
      @pointer-down-outside="onPointerDownOutside"
    >
      <SheetHeader class="sr-only">
        <SheetTitle>{{ $t("Профиль пользователя") }}</SheetTitle>
      </SheetHeader>

      <div class="flex-1 flex min-h-0 overflow-hidden">
        <template v-if="state.loading">
          <aside
            class="w-[290px] shrink-0 bg-card border-r border-border pb-5 flex flex-col gap-2 overflow-hidden"
          >
            <div class="relative overflow-hidden bg-card">
              <div class="h-24 bg-muted border-0 border-b border-border" />
              <div class="px-4 pt-0 pb-2 -mt-10">
                <Skeleton
                  class="w-16 h-16 rounded-full mb-3 ring-4 ring-card"
                />
                <Skeleton class="h-4 w-40 mb-2" />
                <Skeleton class="h-3 w-20" />
              </div>
            </div>
            <div class="flex flex-col py-2">
              <div
                v-for="i in 3"
                :key="i"
                class="flex items-center gap-3.5 px-5 py-2"
              >
                <Skeleton class="h-8 flex-1" />
              </div>
            </div>
          </aside>
          <main class="flex-1 bg-muted/30 overflow-y-auto">
            <div class="max-w-[1080px] mx-auto px-6 py-10 flex flex-col gap-4">
              <Skeleton
                class="h-40 w-full"
                style="border-radius: var(--radius)"
              />
              <Skeleton
                class="h-40 w-full"
                style="border-radius: var(--radius)"
              />
            </div>
          </main>
        </template>

        <template v-else-if="showEmpty">
          <main
            class="flex-1 bg-muted/30 overflow-y-auto flex items-center justify-center"
          >
            <Empty
              :title="$t('Профиль не найден')"
              :description="$t('Не удалось загрузить данные профиля.')"
            >
              <template #footer>
                <Button @click="load(state.userUuid, state.mode)">
                  {{ $t("Повторить") }}
                </Button>
              </template>
            </Empty>
          </main>
        </template>

        <template v-else>
          <UserProfileSidebar
            :data="state.data"
            :tab="tab"
            :edit-mode="state.editMode"
            :saving="state.saving"
            :deleting="state.deleting"
            :organizations="state.organizations"
            :can-edit="state.mode !== 'create'"
            :can-edit-admin="hasAdminPerm"
            :can-delete="hasAdminPerm && state.mode !== 'create'"
            :show-view-switcher="canShowSwitcher"
            @set-tab="tab = $event"
            @start-edit="onEditClick"
            @cancel="onCancel"
            @delete-user="onDeleteUser"
            @switch-view="onSwitchView"
          />

          <main class="flex-1 bg-muted/30 overflow-y-auto">
            <div class="max-w-[1080px] mx-auto px-6 py-10">
              <component
                :is="currentTab"
                v-bind="tabBindings"
                v-on="tabListeners"
              />

              <div v-if="state.editMode" class="flex justify-end gap-2 mt-6">
                <Button variant="outline" size="sm" @click="onCancel">
                  {{ state.mode === "create" ? $t("Отмена") : $t("Отменить") }}
                </Button>
                <Button size="sm" :disabled="!canSave" @click="onSave">
                  <Spinner
                    v-if="state.saving"
                    size="sm"
                    class="mr-1.5 text-current"
                  />
                  {{ saveLabel }}
                </Button>
              </div>
            </div>
          </main>
        </template>
      </div>
    </SheetContent>
  </Sheet>
</template>
